<script lang="ts">
  import Mixins from '@/helpers/mixins';
  import { ButtonTypes } from '@/helpers/enums';
  import { defineComponent } from 'vue';

  export default defineComponent({
    mixins: [Mixins],
    props: {
      confirmed: { type: Boolean, default: false },
      loading: { type: Boolean, default: false },
      active: { type: Boolean, default: false },
      disabled: { type: Boolean, default: false },
      type: { type: String as () => ButtonTypes, default: ButtonTypes.primary },
    },
    emits: ['click'],
    data() {
      return {
        shadowedButtonTypes: [
          ButtonTypes.primary,
          ButtonTypes.confirm,
        ] as Array<ButtonTypes>,
        buttonTouched: false as boolean,
      };
    },
    computed: {
      isConfirmButton(): boolean {
        return this.type === ButtonTypes.confirm;
      },
      buttonConfirmed(): boolean {
        return this.isConfirmButton && this.confirmed;
      },
      buttonLoading(): boolean {
        return this.isConfirmButton && this.loading;
      },
      showShadow(): boolean {
        return this.shadowedButtonTypes.includes(this.type);
      },
    },
    methods: {
      buttonTapHandler(): void {
        this.buttonTouched = true;

        setTimeout(() => (this.buttonTouched = false), 150);
      },
      buttonClick(): void {
        if (this.disabled) {
          return;
        }

        if (this.loading) {
          return;
        }

        this.$emit('click');
      },
    },
  });
</script>

<template>
  <div
    v-touch:tap="buttonTapHandler"
    :class="{
      'button--disabled': disabled,
      'button--active': active,
      'button--shadow': showShadow,
      confirmed: buttonConfirmed,
      'button--touched': buttonTouched,
      [`button--${type}`]: true,
    }"
    class="button button--button box-rounded-8 d-flex align-items-center font-button-1"
    @click="buttonClick"
  >
    <img
      v-if="buttonConfirmed"
      :alt="$t('common.img_alts.confirmed')"
      :src="imgUrl('icons/white_check.svg')"
      height="24"
      width="24"
    />
    <img
      v-else-if="buttonLoading"
      :alt="$t('common.img_alts.loading')"
      :src="imgUrl('spinners/white.svg')"
      height="24"
      width="24"
    />
    <slot v-else />
  </div>
</template>

<style lang="scss" scoped>
  $animation-length: 300ms;

  .button {
    transition: all $animation-length;

    &--shadow {
      box-shadow: 0px 14px 11px -10px $blue-shadow;
    }

    &.button--touched {
      box-shadow: unset;
    }

    &--button {
      cursor: pointer;
      background-color: $blue-cta;
      color: $white;
      padding: 1.75 * $base-unit 2 * $base-unit;
      z-index: 5;
      text-align: center;
      justify-content: center;
    }

    &--primary {
      &.button--touched {
        background-color: $blue;
      }

      &.button--disabled {
        background-color: $gray-light;
        cursor: not-allowed;
        box-shadow: none;
      }
    }

    &--secondary {
      background-color: $white;
      border: 1.5px solid $blue-cta;
      color: $blue-cta;

      &.button--touched {
        background-color: $blue-light-light;
      }

      &.button--disabled {
        border-color: $blue-light;
        background-color: $white;
        color: $blue-light;
        cursor: not-allowed;
        box-shadow: none;
      }
    }

    &--secondary-thick {
      background-color: $white;
      border: 2px solid $blue-cta;
      color: $blue-cta;

      &.button--touched {
        background-color: $blue-light-light;
      }
    }

    &--tertiary {
      @extend .button--secondary;
      border: 1px solid $blue-light;

      &.button--disabled {
        border: 1px solid $gray-light;
        cursor: not-allowed;
      }

      &.button--active {
        background-color: $blue-light-light;
      }
    }

    &--confirm {
      &.button--touched {
        background-color: $blue;
      }

      &.confirmed {
        background-color: $accent-green;
        box-shadow: unset;
        // padding: 1.25 * $base-unit;
      }

      &.button--disabled {
        background-color: $gray-light;
        cursor: not-allowed;
        box-shadow: none;
      }
    }

    &--dark {
      background-color: $gray-black;
      box-shadow: 0px 2px 8px $gray-black-shadow;

      &.button--touched {
        background-color: $gray-dark;
        box-shadow: unset;
      }
    }

    &--dark-outline {
      background-color: $gray-black;
      border: 1px solid $gray-light;
      color: $white;

      &.button--touched {
        background-color: $gray-dark;
        box-shadow: unset;
      }

      &.button--active {
        color: $blue-cta;
        border: 1px solid $blue-cta;
        background-color: $gray-black;
        box-shadow: unset;
      }
    }
  }
</style>
